'use strict';

define('vb/extensions/dynamic/private/helpers/layoutDeserializer',['vb/private/log', 'vb/extensions/dynamic/private/helpers/layoutSerializer'],
  (Log, LayoutSerializer) => {
    const logger = Log.getLogger('/vb/extensions/dynamic/private/helpers/LayoutDeserializer');

    /**
     * Deserializes a layout from a blob and provides access to its options and resources.
     */
    class LayoutDeserializer {
      /**
       * Creates an instance of LayoutDeserializer.
       *
       * @param {Blob} blob - The blob containing the layout data.
       * @param {number} hash - The expected hash of the layout data.
       */
      constructor(blob, hash) {
        this.blob = blob;
        this.hash = hash;
        this.options = {};
        this.resourceMap = {};
      }

      /**
       * Retrieves the layout options.
       *
       * @returns {object} The layout options.
       */
      getOptions() {
        return this.options;
      }

      /**
       * Retrieves the resource map associated with the deserialized layout.
       *
       * @returns {object} The resource map, where keys are resource paths and values are the corresponding resource
       * contents.
       */
      getResourceMap() {
        return this.resourceMap;
      }

      /**
       * Retrieves a text resource from the deserialized layout's resource map.
       *
       * @param {string} path - The path to the text resource.
       * @returns {string} The contents of the text resource, or undefined if the resource is not found.
       */
      getTextResource(path) {
        return this.resourceMap[path];
      }

      /**
       * Retrieves a JSON resource from the deserialized layout's resource map.
       *
       * @param {string} path - The path to the JSON resource.
       * @returns {object|undefined} The parsed JSON contents of the resource, or undefined if the resource is not
       * found or is not valid JSON.
       */
      getJsonResource(path) {
        return JSON.parse(this.resourceMap[path]);
      }

      /**
       * Deserializes the layout data from the provided blob and populates the layout options and resource map.
       *
       * @async
       * @returns {Promise<void>} A promise that resolves when the deserialization is complete.
       */
      deserialize() {
        return this.blob.text().then((dataStr) => {
          const hash = LayoutSerializer.generateHashCode(dataStr);

          if (this.hash !== hash) {
            logger.error('Provided hash', this.hash, 'does not match hash', hash, 'generated from the data.',
              'This layout will not be imported.');
          } else {
            const {
              version,
              options,
              resourceMap,
            } = JSON.parse(dataStr);

            if (version === LayoutSerializer.VERSION) {
              if (!options) {
                logger.warn('Missing metadata descriptor options in the exported layout.'
                  + ' Imported layout will not function properly.');
              } else {
                // options is stored as a stringified JSON
                this.options = JSON.parse(options);
              }

              this.resourceMap = resourceMap || {};

              logger.info('Deserialized layout: version', version, this.options, this.resourceMap);
            } else {
              logger.error('Unsupported exported layout with version', version,
                'Imported layout will not function property.');
            }
          }
        }).catch((error) => {
          logger.error('Failed to import layout.', error);
        });
      }

      /**
       * Releases the resources held by this LayoutDeserializer instance.
       *
       * This method sets the blob, options, and resource map to null, allowing for garbage collection.
       */
      dispose() {
        this.blob = null;
        this.options = null;
        this.resourceMap = null;
      }
    }

    return LayoutDeserializer;
  });

